/**
 * Record of Processing Activities — PDF report builder.
 *
 * Pick the activities and the sections, get an A4 PDF that mirrors what the detail page
 * shows. Mirrors `AiUseCaseReport.tsx`'s shape closely — same reasons apply: kept as its own
 * page rather than bolting multi-select onto `RegisterItemList`, which is shared by six
 * other registers and this is a report surface, not a list behaviour.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { processingActivityApi, peopleApi, departmentsApi } from '../../services/api';
import type { ProcessingActivity, ActivityStatus, ProcessorRole, DpiaVerdict } from '../../types/models';
import {
  ACTIVITY_STATUSES, ACTIVITY_STATUS_LABELS, ACTIVITY_STATUS_TONE,
  PROCESSOR_ROLES, PROCESSOR_ROLE_LABELS,
  DPIA_VERDICT_LABELS, DPIA_VERDICT_TONE,
} from '../../constants/privacy';
import { exportProcessingActivitiesPdf, ALL_SECTIONS, type PdfSections } from '../../utils/processingActivityPdf';
import { useToast } from '../../hooks/useToast';

// Order matches the order the sections appear in the document.
const SECTION_LABELS: Array<{ key: keyof PdfSections; label: string; hint: string }> = [
  { key: 'identity',     label: 'Identity and role',          hint: 'Name, role, joint controllers, owner, department, description' },
  { key: 'purpose',      label: 'Purpose and lawful basis',   hint: 'Purposes, Article 6 basis, the legitimate-interests test, the Article 9(2) condition' },
  { key: 'dataSubjects', label: 'Data subjects',              hint: 'Categories of data subject, approximate volume, vulnerable groups' },
  { key: 'dataFields',   label: 'Categories of personal data', hint: 'The data field inventory — what, and how sensitive' },
  { key: 'recipients',   label: 'Recipients',                 hint: 'Who else sees this data, and which processors' },
  { key: 'transfers',    label: 'Transfers outside the EEA',  hint: 'Country, recipient, Chapter V mechanism, safeguards' },
  { key: 'retention',    label: 'Retention',                  hint: 'Time limits for erasure, per category of data' },
  { key: 'security',     label: 'Security measures',          hint: 'Technical and organisational measures' },
  { key: 'governance',   label: 'Record history',             hint: 'Status, created and last-updated timestamps' },
];

export const ProcessingActivityReport: React.FC = () => {
  const toast = useToast();
  const { data: activities = [], isLoading, error } = useQuery<ProcessingActivity[]>({
    queryKey: ['list', '/processing-activities'],
    queryFn: () => processingActivityApi.list(),
  });

  // Two id spaces appear on a processing activity — `ownerId` (a personId) and
  // `departmentId` — and neither register can resolve the other's ids. Both are fetched
  // here, where there is a query client, and passed to the PDF builder as one map: that
  // module has no data access and would otherwise have to print raw ids.
  const { data: people = [] } = useQuery({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const { data: departments = [] } = useQuery<Array<{ departmentId?: string; name?: string }>>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });

  const names = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (people as Array<{ personId?: string; personName?: string }>).forEach((p) => {
      if (p.personId && p.personName) map[p.personId] = p.personName;
    });
    departments.forEach((d) => {
      if (d.departmentId && d.name) map[d.departmentId] = d.name;
    });
    // A blank name would resolve as "found but empty" and print nothing at all, which is
    // worse than printing the id under an honest label.
    Object.keys(map).forEach((id) => { if (!map[id]) delete map[id]; });
    return map;
  }, [people, departments]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sections, setSections] = useState<PdfSections>(ALL_SECTIONS);
  const [onePerPage, setOnePerPage] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  // 'any' rather than '' is the no-filter sentinel: '' is a real verdict here ("not
  // screened"), and finding the unscreened ones is one of the two things people come to
  // this page for.
  const [verdictFilter, setVerdictFilter] = useState<DpiaVerdict | 'any'>('any');

  const visible = useMemo(
    () => activities.filter(a =>
      (!statusFilter || a.status === statusFilter) &&
      (!roleFilter || a.role === roleFilter) &&
      (verdictFilter === 'any' || (a.dpiaVerdict || '') === verdictFilter)),
    [activities, statusFilter, roleFilter, verdictFilter],
  );

  const allVisibleSelected = visible.length > 0 && visible.every(a => selected.has(a.activityId));

  const toggle = (id: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAllVisible = () =>
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) visible.forEach(a => next.delete(a.activityId));
      else visible.forEach(a => next.add(a.activityId));
      return next;
    });

  const chosen = useMemo(
    () => activities.filter(a => selected.has(a.activityId)),
    [activities, selected],
  );

  const anySection = Object.values(sections).some(Boolean);

  const handleExport = () => {
    if (!chosen.length) { toast.error('Select at least one processing activity to include.'); return; }
    if (!anySection)    { toast.error('Select at least one section to include.'); return; }
    try {
      exportProcessingActivitiesPdf(chosen, sections, { onePerPage, names });
      toast.success(`Exported ${chosen.length} processing activit${chosen.length === 1 ? 'y' : 'ies'} to PDF.`);
    } catch (e) {
      toast.error(`Could not generate the PDF: ${(e as Error).message}`);
    }
  };

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-file-pdf-2-line"
        title="Processing Activities Report"
        subtitle="Select processing activities and export them as a print-ready PDF"
        colour="primary"
        actions={
          <>
            <Link to="/processing-activities" className="np-btn np-btn-ghost">
              <i className="ri-arrow-left-line"></i>Back to register
            </Link>
            <button
              type="button"
              className="np-btn np-btn-primary"
              onClick={handleExport}
              disabled={!chosen.length || !anySection}
            >
              <i className="ri-download-2-line"></i>
              Export PDF{chosen.length ? ` (${chosen.length})` : ''}
            </button>
          </>
        }
      />

      {error ? (
        <div className="np-banner np-tone-danger">
          <i className="ri-error-warning-line"></i>Could not load the register.
        </div>
      ) : isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="np-grid np-grid-8-4">
          {/* ── Selection ── */}
          <div className="np-card">
            <div className="np-card-head">
              <h2><i className="ri-list-check-2"></i>Processing activities</h2>
              <span className="np-chip np-tone-neutral">
                {chosen.length} of {activities.length} selected
              </span>
            </div>

            <div className="np-toolbar">
              <select className="np-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                {ACTIVITY_STATUSES.map(s => (
                  <option key={s} value={s}>{ACTIVITY_STATUS_LABELS[s]}</option>
                ))}
              </select>
              <select className="np-select" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                <option value="">All roles</option>
                {PROCESSOR_ROLES.map(r => (
                  <option key={r} value={r}>{PROCESSOR_ROLE_LABELS[r]}</option>
                ))}
              </select>
              <select
                className="np-select"
                value={verdictFilter}
                onChange={e => setVerdictFilter(e.target.value as DpiaVerdict | 'any')}
                aria-label="Filter by DPIA verdict"
              >
                <option value="any">All DPIA verdicts</option>
                <option value="">{DPIA_VERDICT_LABELS['']}</option>
                <option value="required">{DPIA_VERDICT_LABELS.required}</option>
                <option value="not_required">{DPIA_VERDICT_LABELS.not_required}</option>
                <option value="discretionary">{DPIA_VERDICT_LABELS.discretionary}</option>
                <option value="incomplete">{DPIA_VERDICT_LABELS.incomplete}</option>
              </select>
              <button type="button" className="np-btn np-btn-ghost np-btn-sm" onClick={toggleAllVisible} disabled={!visible.length}>
                <i className={allVisibleSelected ? 'ri-checkbox-blank-line' : 'ri-checkbox-multiple-line'}></i>
                {allVisibleSelected ? 'Clear these' : 'Select these'}
              </button>
            </div>

            <div className="np-card-body p-0">
              {!visible.length ? (
                <div className="text-center py-5">
                  <i className="ri-database-2-line ri-36px d-block mb-2" style={{ color: 'var(--bs-secondary-color)' }}></i>
                  <p className="np-sub mb-0">
                    {activities.length ? 'No processing activities match those filters.' : 'The register is empty.'}
                  </p>
                </div>
              ) : (
                <div className="np-table-wrap">
                  <table className="np-table">
                    <thead>
                      <tr>
                        <th style={{ width: '44px' }}></th>
                        <th style={{ width: '96px' }}>ID</th>
                        <th>Processing activity</th>
                        <th style={{ width: '116px' }}>Role</th>
                        <th style={{ width: '132px' }}>DPIA</th>
                        <th style={{ width: '104px' }}>Status</th>
                        <th style={{ width: '76px' }}>Fields</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map(a => {
                        const isOn = selected.has(a.activityId);
                        // 'customer_controlled' counts as a real personal-data signal here too.
                        const personal = (a.dataFields ?? []).filter(f => f.isPersonalData !== 'no').length;
                        const status = (a.status || 'draft') as ActivityStatus;
                        const verdict = (a.dpiaVerdict || '') as DpiaVerdict;
                        return (
                          <tr
                            key={a.activityId}
                            onClick={() => toggle(a.activityId)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={isOn}
                                onChange={() => toggle(a.activityId)}
                                onClick={e => e.stopPropagation()}
                                aria-label={`Include ${a.name || a.activityId}`}
                              />
                            </td>
                            <td className="np-tabular">{a.activityId}</td>
                            <td className="np-row-title">{a.name || <span className="np-hint">Untitled draft</span>}</td>
                            <td>
                              <span className="np-chip np-tone-neutral">
                                {PROCESSOR_ROLE_LABELS[(a.role || 'controller') as ProcessorRole] ?? a.role}
                              </span>
                            </td>
                            <td>
                              {/* Never dashed out — an unscreened activity is exactly the one
                                  worth finding via this filter/report. */}
                              <span className={`np-chip np-tone-${DPIA_VERDICT_TONE[verdict] ?? 'neutral'}`}>
                                {DPIA_VERDICT_LABELS[verdict] ?? 'Not screened'}
                              </span>
                            </td>
                            <td>
                              <span className={`np-chip np-tone-${ACTIVITY_STATUS_TONE[status] ?? 'neutral'}`}>
                                {ACTIVITY_STATUS_LABELS[status] ?? a.status}
                              </span>
                            </td>
                            <td className="np-hint np-tabular">
                              {(a.dataFields ?? []).length}
                              {personal > 0 && <span className="np-chip np-tone-warning ms-1">{personal}p</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* ── Options ── */}
          <div>
            <div className="np-card mb-4">
              <div className="np-card-head">
                <h2><i className="ri-layout-row-line"></i>Sections</h2>
              </div>
              <div className="np-card-body">
                {SECTION_LABELS.map(({ key, label, hint }) => (
                  <label key={key} className="d-flex align-items-start gap-2 mb-3" style={{ cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={sections[key]}
                      onChange={e => setSections(prev => ({ ...prev, [key]: e.target.checked }))}
                      style={{ marginTop: '0.2rem' }}
                    />
                    <span>
                      <span className="d-block" style={{ fontWeight: 600, color: 'var(--bs-heading-color)' }}>{label}</span>
                      <span className="np-hint">{hint}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="np-card mb-4">
              <div className="np-card-head">
                <h2><i className="ri-file-copy-2-line"></i>Layout</h2>
              </div>
              <div className="np-card-body">
                <label className="d-flex align-items-start gap-2" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={onePerPage}
                    onChange={e => setOnePerPage(e.target.checked)}
                    style={{ marginTop: '0.2rem' }}
                  />
                  <span>
                    <span className="d-block" style={{ fontWeight: 600, color: 'var(--bs-heading-color)' }}>
                      Start each activity on a new page
                    </span>
                    <span className="np-hint">
                      Recommended when the report is being circulated per activity. Turn off to
                      pack them continuously.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            <div className="np-banner np-tone-info">
              <i className="ri-information-line"></i>
              <span>
                Text in the PDF is selectable and searchable, and content is never split across a
                page break — table rows and headings move whole.
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessingActivityReport;
